if (!customElements.get('collection-pagination')) {
  const navigationType = performance.getEntriesByType('navigation')[0]?.type;
  let stateHandled = false;

  customElements.define(
    'collection-pagination',
    class CollectionPagination extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.saveScrollPosition = this.saveScrollPosition.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
        window.addEventListener('pagehide', this.saveScrollPosition);

        if (this.dataset.type === 'infinite') {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
            },
            { rootMargin: '0px 0px 600px 0px' }
          );
        }

        // Only the first render after a page load restores state; later renders come from filter or sort changes.
        if (!stateHandled) {
          stateHandled = true;
          if (navigationType === 'back_forward') {
            this.restoreState();
            return;
          }
          sessionStorage.removeItem(this.storageKey);
        }

        this.observer?.observe(this);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        window.removeEventListener('pagehide', this.saveScrollPosition);
        this.observer?.disconnect();
        this.abortController?.abort();
      }

      get storageKey() {
        return `collection-pagination:${window.location.pathname}${window.location.search}`;
      }

      get nextLink() {
        return this.querySelector('[data-next-link]');
      }

      get grid() {
        return document.getElementById('product-grid');
      }

      onClick(event) {
        if (!event.target.closest('[data-next-link]')) return;
        event.preventDefault();
        this.loadNext({ focusNewItems: true });
      }

      async loadNext({ focusNewItems = false } = {}) {
        const link = this.nextLink;
        if (this.loading || !link) return false;

        this.setLoading(true);
        this.abortController = new AbortController();

        try {
          const url = new URL(link.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const newItems = Array.from(html.querySelectorAll('#product-grid > .grid__item'));
          newItems.forEach((item) => item.classList.add('scroll-trigger--cancel'));
          this.grid.append(...newItems);

          const loadedPage = Number(url.searchParams.get('page'));
          const nextHref = html.querySelector('collection-pagination [data-next-link]')?.getAttribute('href');
          if (nextHref) {
            link.setAttribute('href', nextHref);
          } else {
            link.remove();
            this.observer?.disconnect();
          }

          this.updateStatus();
          this.saveState({ page: loadedPage });
          this.classList.remove('collection-pagination--error');

          if (focusNewItems) newItems[0]?.querySelector('a, button')?.focus({ preventScroll: true });
          return true;
        } catch (error) {
          if (error.name === 'AbortError') return false;
          console.error(error);
          // Fall back to the button so infinite scroll does not silently stall.
          this.classList.add('collection-pagination--error');
          this.observer?.disconnect();
          return false;
        } finally {
          this.setLoading(false);
          // Re-observing fires a fresh intersection check, in case the sentinel is still in view.
          if (this.observer && this.nextLink && !this.classList.contains('collection-pagination--error')) {
            this.observer.unobserve(this);
            this.observer.observe(this);
          }
        }
      }

      async restoreState() {
        const state = this.readState();
        if (state?.page) {
          while (this.nextPage && this.nextPage <= state.page) {
            if (!(await this.loadNext())) break;
          }
          if (typeof state.scrollY === 'number') window.scrollTo(0, state.scrollY);
        }
        this.observer?.observe(this);
      }

      get nextPage() {
        const link = this.nextLink;
        return link ? Number(new URL(link.href).searchParams.get('page')) : null;
      }

      readState() {
        try {
          return JSON.parse(sessionStorage.getItem(this.storageKey));
        } catch (error) {
          return null;
        }
      }

      saveState(changes) {
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify({ ...this.readState(), ...changes }));
        } catch (error) {
          // Storage can be unavailable (private mode, quota); restoring is a nice-to-have.
        }
      }

      saveScrollPosition() {
        if (this.readState()?.page) this.saveState({ scrollY: window.scrollY });
      }

      updateStatus() {
        const status = this.querySelector('.collection-pagination__status');
        if (!status) return;
        status.textContent = this.dataset.statusTemplate
          .replace('[shown]', this.grid.querySelectorAll(':scope > .grid__item').length)
          .replace('[total]', this.dataset.total);
      }

      setLoading(loading) {
        this.loading = loading;
        this.setAttribute('aria-busy', loading);
        this.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.toggle('hidden', !loading));
        this.nextLink?.classList.toggle('loading', loading);
      }
    }
  );
}
